import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Box } from '@mui/material';

/**
 * Builds a human-readable page name from the current pathname.
 *
 * Examples:
 *   /                       → "Home"
 *   /transactions           → "Transactions"
 *   /customer-service       → "Customer Service"
 *   /catalog/create         → "Catalog Create"
 *   /catalog/abc-123        → "Catalog Detail"
 *   /iar/run-456            → "IAR Detail"
 *   /property-book/reserved-stock → "Reserved Stock"
 */
function derivePageName(pathname: string): string {
  if (pathname === '/') return 'Home';

  const segments = pathname.replace(/^\//, '').split('/').filter(Boolean);

  // UUID-like last segment → treat as a detail page
  const last = segments[segments.length - 1];
  const isId = /^[0-9a-f-]{8,}$/i.test(last ?? '');
  if (isId && segments.length >= 2) {
    const parent = segments[segments.length - 2] ?? 'Item';
    return `${humanize(parent)} Detail`;
  }

  // Use the last meaningful segment
  const label = segments[segments.length - 1] ?? 'Page';
  return humanize(label);
}

function humanize(slug: string): string {
  return slug
    .replace(/[-_]/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * An ARIA live region that announces route changes to screen readers.
 *
 * It renders a visually-hidden `<div role="status" aria-live="polite">` whose
 * text content is updated whenever the React Router location changes, causing
 * assistive technology to read the new page name.
 */
export const RouteAnnouncer: React.FC = () => {
  const location = useLocation();
  const [announcement, setAnnouncement] = useState('');
  const prevPathRef = useRef(location.pathname);

  useEffect(() => {
    // Skip the initial mount — only announce *changes*.
    if (prevPathRef.current === location.pathname) return;
    prevPathRef.current = location.pathname;

    const pageName = derivePageName(location.pathname);
    setAnnouncement(`Navigated to ${pageName}`);
  }, [location.pathname]);

  return (
    <Box
      role="status"
      aria-live="polite"
      aria-atomic="true"
      sx={{
        position: 'absolute',
        width: '1px',
        height: '1px',
        padding: 0,
        margin: '-1px',
        overflow: 'hidden',
        clip: 'rect(0, 0, 0, 0)',
        whiteSpace: 'nowrap',
        border: 0,
      }}
    >
      {announcement}
    </Box>
  );
};
